import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box, Typography, Paper, Chip, TextField, InputAdornment, FormControl, InputLabel,
  Select, MenuItem, Grid, Card, CardContent, Avatar, Stack, IconButton, Tooltip,
  LinearProgress, alpha, useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import { authToken } from '../globals/Global';
import { getPaymentDetails } from '../globals/Services';

export default function PaymentManagement() {
  const theme = useTheme();
  const [paymentRows, setPaymentRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('All');

  // Statistics state
  const [stats, setStats] = useState({
    totalPayments: 0,
    totalAmount: 0,
    pendingPayments: 0,
    successfulPayments: 0,
    pendingAmount: 0,
    successfulAmount: 0
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

const getPaymentStatusStyles = (status) => {
  switch (status?.toLowerCase()) {
    case 'success':
      return {
        bgcolor: '#e8f5e8',           // Light green background
        color: '#2e7d2e',             // Dark green text
        border: '1px solid #4caf50'   // Green border
      };
    case 'pending':
      return {
        bgcolor: '#fff3e0',           // Light orange background
        color: '#eb9800ff',             // Dark orange text
        border: '1px solid #FFA500'   // Orange border
      };
    case 'failed':
      return {
        bgcolor: '#ffebee',           // Light red background
        color: '#c62828',             // Dark red text
        border: '1px solid #f44336'   // Red border
      };
    default:
      return {
        bgcolor: '#ffefefff',           // Light grey background
        color: '#e70000ff',             // Dark grey text
        border: '1px solid #FF0000'   // Grey border
      };
  }
};

  const getPaymentTypeColor = (type) => {
    switch (type?.toUpperCase()) {
      case 'ONLINE':
        return 'primary';
      case 'OFFLINE':
        return 'secondary';
      case 'CASH':
        return 'info';
      default:
        return 'default';
    }
  };

  const calculateStats = (payments) => {
    const totalPayments = payments.length;
    const totalAmount = payments.reduce((sum, payment) => sum + payment.totalAmount, 0);
    
    const pendingPayments = payments.filter(p => p.paymentStatus?.toLowerCase() === 'pending');
    const successfulPayments = payments.filter(p => p.paymentStatus?.toLowerCase() === 'success');
    
    const pendingAmount = pendingPayments.reduce((sum, payment) => sum + payment.totalAmount, 0);
    const successfulAmount = successfulPayments.reduce((sum, payment) => sum + payment.totalAmount, 0);

    setStats({
      totalPayments,
      totalAmount,
      pendingPayments: pendingPayments.length,
      successfulPayments: successfulPayments.length,
      pendingAmount,
      successfulAmount
    });
  };

  const fetchPaymentData = React.useCallback(async () => {
    try {
      setLoading(true);
      const token = authToken || localStorage.getItem('sessionToken');

      const response = await axios.get(getPaymentDetails, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const payments = response.data?.data || [];
      
      // Sort payments by payment date (newest first)
      const sortedPayments = payments.sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate));
      
      const mapped = sortedPayments.map((payment, index) => ({
        id: payment.paymentId,
        paymentId: payment.paymentId,
        orderId: payment.orderId,
        userId: payment.userId,
        paymentStatus: payment.paymentStatus,
        paymentType: payment.paymentType,
        txnId: payment.txnId || 'N/A',
        totalAmount: payment.totalAmount,
        paymentDate: payment.paymentDate,
        formattedDate: formatDateTime(payment.paymentDate),
        formattedAmount: formatCurrency(payment.totalAmount)
      }));

      setPaymentRows(mapped);
      setFilteredRows(mapped);
      calculateStats(mapped);

    } catch (error) {
      console.error('Error loading payment data:', error);
      setPaymentRows([]);
      setFilteredRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter payments based on search term, status, and payment type
  useEffect(() => {
    let filtered = paymentRows;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(payment => 
        payment.paymentId.toString().includes(searchTerm) ||
        payment.orderId.toString().includes(searchTerm) ||
        payment.userId.toString().includes(searchTerm) ||
        payment.txnId.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'All') {
      filtered = filtered.filter(payment => 
        payment.paymentStatus?.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    // Payment type filter
    if (paymentTypeFilter !== 'All') {
      filtered = filtered.filter(payment => 
        payment.paymentType?.toUpperCase() === paymentTypeFilter.toUpperCase()
      );
    }

    setFilteredRows(filtered);
  }, [searchTerm, statusFilter, paymentTypeFilter, paymentRows]);

  useEffect(() => {
    fetchPaymentData();
  }, [fetchPaymentData]);

  const StatCard = ({ title, value, amount, icon, color, percentage }) => (
    <Card 
      elevation={0} 
      sx={{ 
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.1)} 0%, ${alpha(theme.palette[color].main, 0.05)} 100%)`,
        border: `1px solid ${alpha(theme.palette[color].main, 0.2)}`,
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 32px ${alpha(theme.palette[color].main, 0.15)}`,
        },
        transition: 'all 0.3s ease-in-out'
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Avatar 
            sx={{ 
              bgcolor: alpha(theme.palette[color].main, 0.1),
              color: theme.palette[color].main,
              width: 56,
              height: 56
            }}
          >
            {icon}
          </Avatar>
          {percentage && (
            <Chip 
              icon={<TrendingUpIcon />}
              label={`${percentage}%`}
              size="small"
              sx={{ 
                bgcolor: alpha(theme.palette.success.main, 0.1),
                color: theme.palette.success.main,
                fontWeight: 'bold'
              }}
            />
          )}
        </Box>
        
        <Typography variant="h3" fontWeight="700" color={`${color}.main`} mb={1}>
          {value}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" mb={2} fontWeight="500">
          {title}
        </Typography>
        
        {amount && (
          <Box>
            <Typography variant="h6" color="text.primary" fontWeight="600">
              {amount}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={percentage || 75} 
              sx={{ 
                mt: 1,
                height: 6,
                borderRadius: 3,
                bgcolor: alpha(theme.palette[color].main, 0.1),
                '& .MuiLinearProgress-bar': {
                  bgcolor: theme.palette[color].main
                }
              }}
            />
          </Box>
        )}
      </CardContent>
      
      {/* Decorative element */}
      <Box
        sx={{
          position: 'absolute',
          top: -10,
          right: -10,
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: alpha(theme.palette[color].main, 0.1),
        }}
      />
    </Card>
  );

  const columns = [
    { 
      field: 'paymentId', 
      headerName: 'Payment ID', 
      width: 110, 
      headerAlign: 'center', 
      align: 'center',
      renderHeader: () => <Typography fontWeight="700" fontSize="0.875rem">Payment ID</Typography>,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" justifyContent="center">
          <Chip 
            label={`#${params.value}`}
            size="small"
            sx={{ 
              fontFamily: 'monospace', 
              fontWeight: 'bold',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main
            }}
          />
        </Box>
      )
    },
    { 
      field: 'orderId', 
      headerName: 'Order ID', 
      width: 100, 
      headerAlign: 'center', 
      align: 'center',
      renderHeader: () => <Typography fontWeight="700" fontSize="0.875rem">Order ID</Typography>,
      renderCell: (params) => (
        <Typography 
          variant="body2" 
          fontWeight="600"
          sx={{ color: 'text.primary' }}
        >
          #{params.value}
        </Typography>
      )
    },
    { 
      field: 'userId', 
      headerName: 'User', 
      width: 100, 
      headerAlign: 'center', 
      align: 'center',
      renderHeader: () => <Typography fontWeight="700" fontSize="0.875rem">User</Typography>,
      renderCell: (params) => (
        <Box display="flex" justifyContent="center" alignItems="center" width="100%">
          <Avatar 
            sx={{ 
              width: 32, 
              height: 32, 
              bgcolor: alpha(theme.palette.info.main, 0.1),
              color: theme.palette.info.main,
              fontSize: '0.75rem',
              fontWeight: 'bold',
            }}
          >
            {params.value}
          </Avatar>
        </Box>
      )
    },
    {
      field: 'paymentStatus',
      headerName: 'Status',
      width: 130,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => <Typography fontWeight="700" fontSize="0.875rem">Payment Status</Typography>,
      renderCell: (params) => {
        return (
          <Box display="flex" justifyContent="center" alignItems="center" width="100%">
            <Chip
              label={params.value}
              size="small"
              variant="filled"
              sx={{ 
                fontWeight: 'bold',
                minWidth: 80,
                ...getPaymentStatusStyles(params.value),  // Use custom styles
                '& .MuiChip-label': {
                  px: 2
                }
              }}
            />
          </Box>
        );
      },
    },
    {
      field: 'paymentType',
      headerName: 'Type',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => <Typography fontWeight="700" fontSize="0.875rem">Payment Type</Typography>,
      renderCell: (params) => (
        <Box display="flex" justifyContent="center" alignItems="center" width="100%">
          <Chip
            label={params.value}
            color={getPaymentTypeColor(params.value)}
            size="small"
            variant="outlined"
            sx={{ 
              fontWeight: 'bold',
              borderWidth: 2
            }}
          />
        </Box>
      ),
    },
    { 
      field: 'txnId', 
      headerName: 'Transaction ID', 
      width: 150, 
      headerAlign: 'center', 
      align: 'center',
      renderHeader: () => <Typography fontWeight="700" fontSize="0.875rem">Transaction ID</Typography>,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" justifyContent="center" width="100%">
          <Typography 
            variant="body2" 
            sx={{ 
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              bgcolor: params.value === 'N/A' ? alpha(theme.palette.grey[500], 0.1) : alpha(theme.palette.success.main, 0.1),
              color: params.value === 'N/A' ? 'text.secondary' : theme.palette.success.main,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              fontWeight: 'bold',
            }}
          >
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'formattedAmount',
      headerName: 'Amount',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => <Typography fontWeight="700" fontSize="0.875rem">Amount</Typography>,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          <CurrencyRupeeIcon 
            sx={{ 
              fontSize: 16, 
              color: theme.palette.success.main,
            }} 
          />
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 'bold',
              color: theme.palette.success.main,
              fontSize: '0.95rem'
            }}
          >
            {params.value.replace('₹', '')}
          </Typography>
        </Box>
      )
    },
    { 
      field: 'formattedDate', 
      headerName: 'Payment Date', 
      width: 160, 
      headerAlign: 'center', 
      align: 'center',
      renderHeader: () => <Typography fontWeight="700" fontSize="0.875rem">Payment Date</Typography>,
      renderCell: (params) => (
        <Box textAlign="center">
          <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: '600' }}>
            {params.value.split(',')[0]}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.value.split(',')[1]?.trim()}
          </Typography>
        </Box>
      )
    },
  ];

  const successRate = stats.totalPayments > 0 ? Math.round((stats.successfulPayments / stats.totalPayments) * 100) : 0;

  return (
    <Box sx={{ 
      p: 3, 
      bgcolor: 'grey.50',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 3
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
              <PaymentIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight="700" mb={0.5}>
                Payment Management
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Monitor and manage all payment transactions
              </Typography>
            </Box>
          </Box>
          
          <Stack direction="row" spacing={2}>
            <Tooltip title="Refresh Data">
              <IconButton 
                onClick={fetchPaymentData}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.1)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Paper>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Payments"
            value={stats.totalPayments}
            amount={formatCurrency(stats.totalAmount)}
            icon={<AccountBalanceWalletIcon />}
            color="primary"
            percentage={85}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Successful Payments"
            value={stats.successfulPayments}
            amount={formatCurrency(stats.successfulAmount)}
            icon={<CheckCircleIcon />}
            color="success"
            percentage={successRate}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Payments"
            value={stats.pendingPayments}
            amount={formatCurrency(stats.pendingAmount)}
            icon={<PendingActionsIcon />}
            color="warning"
            percentage={100 - successRate}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Success Rate"
            value={`${successRate}%`}
            amount={`${stats.successfulPayments}/${stats.totalPayments} payments`}
            icon={<TrendingUpIcon />}
            color="info"
            percentage={successRate}
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight="600" mb={3} color="text.primary">
          Filter & Search
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search Payments"
              placeholder="Payment ID, Order ID, User ID, Transaction ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Payment Status"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="All">All Statuses</MenuItem>
                <MenuItem value="Success">✅ Success</MenuItem>
                <MenuItem value="Pending">⏳ Pending</MenuItem>
                <MenuItem value="Failed">❌ Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Payment Type</InputLabel>
              <Select
                value={paymentTypeFilter}
                onChange={(e) => setPaymentTypeFilter(e.target.value)}
                label="Payment Type"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="All">All Types</MenuItem>
                <MenuItem value="ONLINE">💳 Online</MenuItem>
                <MenuItem value="OFFLINE">🏪 Offline</MenuItem>
                <MenuItem value="CASH">💵 Cash</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Grid */}
      <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ p: 3, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" fontWeight="600" mb={0.5}>
                Payment Records
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredRows.length} of {paymentRows.length} payments
              </Typography>
            </Box>
            <Chip 
              label={`${filteredRows.length} Records`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 'bold' }}
            />
          </Box>
        </Box>
        
        <Box sx={{ height: 650 }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            disableColumnMenu
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 },
              },
              sorting: {
                sortModel: [{ field: 'paymentDate', sort: 'desc' }],
              },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            loading={loading}
            sx={{
              '& .MuiDataGrid-root': {
                border: 'none',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                py: 1.5,
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'grey.50',
                borderBottom: '2px solid rgba(224, 224, 224, 1)',
                minHeight: '56px !important',
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                },
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '2px solid rgba(224, 224, 224, 1)',
                backgroundColor: 'grey.50',
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
}